import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedRef } from 'react-native-reanimated';
import { useScrollToTop } from '@react-navigation/native';
import { addPeopleToGroup, getGroupById } from 'actions';
import { rem, CardView, SettingsItem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system';
import ListEmptyView from 'design-system/ListEmptyView';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectGroups } from 'reducers/selectors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { IconFatPlus } from 'design-system/icons';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import Modal from 'utils/packages/Modal';
import { formatPlural } from 'utils';
import { getFirstNameToShow } from 'features/contacts/selectors';
import GroupCard from './GroupCard';
import { useAddGroupModal } from '../hooks/useAddGroupModal';
const strings = {
    emptyViewTitle: 'Welcome to groups',
    emptyViewSubTitle: 'Trying to work out who owes what for lunch, drinks or household bills? Create a group in Emma to track, split and settle shared expenses',
};
const keyExtractor = (group) => group.id.toString();
const GroupsList = ({ contact, transactions, isSelectGroup, onScroll, isInTab, addTransactionCallback, }) => {
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const isAddingTransaction = transactions?.length !== undefined;
    const handleAddTransactions = useCallback((group, transactions) => {
        const onAdd = (transactions) => {
            dispatch(getGroupById(group.id));
            navigation.navigate('GroupExpense', {
                groupId: group.id,
                transactions,
                addTransactionCallback,
            });
        };
        const transactionsInGroup = transactions.filter((transaction) => 'spendingGroupInfo' in transaction &&
            transaction.spendingGroupInfo?.groups?.some((g) => g.group.id === group.id));
        if (!transactionsInGroup.length) {
            onAdd(transactions);
            return;
        }
        if (transactions.length === transactionsInGroup.length) {
            Modal.showError(formatPlural('This transaction is already added to this group', 'These transactions are already added to this group', transactions.length), undefined, 'Added transaction');
            return;
        }
        Modal.showConfirmation({
            title: 'Added transaction',
            subtitle: 'Some of the transactions you selected are already in this group. Do you want to add the remaining ones?',
            confirmButtonTitle: 'No, thanks',
            cancelButtonTitle: 'Yes, let me add them',
            onPressCancel: () => {
                const transactionsToAdd = transactions.filter((t) => !('spendingGroupInfo' in t &&
                    t.spendingGroupInfo?.groups?.some((g) => g.group.id === group.id)));
                onAdd(transactionsToAdd);
            },
        });
    }, [dispatch, navigation, addTransactionCallback]);
    const onPressGroup = useCallback(async (group) => {
        if (isSelectGroup && contact?.handle) {
            await dispatch(addPeopleToGroup(group.id, [contact.handle]));
            dispatch(getGroupById(group.id));
            navigation.navigate('Success', {
                title: `${contact.nameToShow ??
                    getFirstNameToShow(contact) ??
                    `@${contact.handle}`} was added to the ${group.name} group!`,
                subTitle: '',
                onContinue: () => {
                    navigation.pop(2);
                    return true;
                },
            });
        }
        else if (isAddingTransaction) {
            handleAddTransactions(group, transactions);
        }
        else {
            navigation.navigate('Group', { id: group.id });
        }
    }, [
        isSelectGroup,
        contact,
        isAddingTransaction,
        handleAddTransactions,
        transactions,
    ]);
    const groups = useAppSelector(selectGroups);
    const data = useMemo(() => {
        if (isAddingTransaction) {
            return groups.filter((group) => group.userIds.length > 1);
        }
        return groups;
    }, [groups, isAddingTransaction]);
    const renderItem = useCallback(({ item: group }) => (<GroupCard group={group} onPress={() => onPressGroup(group)} showAmount={!isSelectGroup}/>), [isSelectGroup, onPressGroup]);
    const { show, onCreateGroup } = useAddGroupModal();
    const ListHeaderComponent = useMemo(() => (<TouchableBounceCard noMarginTop onDark style={styles.container} onPress={isSelectGroup || isAddingTransaction ? onCreateGroup : show}>
        <SettingsItem renderIcon={() => (<View style={styles.plusIconContainer}>
              <IconFatPlus colorKey="brand"/>
            </View>)} title={isSelectGroup || isAddingTransaction
            ? 'Create new group'
            : 'Add new group'}/>
      </TouchableBounceCard>), [
        isAddingTransaction,
        isSelectGroup,
        onCreateGroup,
        show,
        styles.container,
        styles.plusIconContainer,
    ]);
    const ListEmptyComponent = useMemo(() => (<CardView noMarginTop withPadding style={styles.emptyCard}>
        <ListEmptyView compact happy subTitlePadding={false} title={strings.emptyViewTitle} subTitle={strings.emptyViewSubTitle}/>
      </CardView>), [styles.emptyCard]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isInTab);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle, !isInTab ? styles.container : undefined);
    const scrollRef = useAnimatedRef();
    useScrollToTop(scrollRef);
    return (<Animated.FlatList ref={scrollRef} data={data} onScroll={onScroll} renderItem={renderItem} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle} ListHeaderComponent={ListHeaderComponent} ListEmptyComponent={ListEmptyComponent} keyExtractor={keyExtractor}/>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        marginTop: rem(8),
    },
    emptyCard: {
        paddingVertical: rem(24),
    },
    plusIconContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        width: rem(44),
        height: rem(44),
        backgroundColor: colors.background.dark,
        borderRadius: rem(44),
        marginRight: rem(16),
    },
}));
export default GroupsList;
